/**
 * Utilities to inject extra columns into QBR table HTML
 * before passing it to TipTap editors.
 */

/**
 * Injects extra header and body columns into a QBR HTML table string.
 *
 * For PersonalQBR: adds Score + Score Rationale columns after all existing columns.
 * For TeamQBR: keeps only first N columns, then adds owner score columns + calculated columns.
 */
export function injectPersonalColumns(html: string): string {
  if (!html) return html;

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const table = doc.querySelector("table");
  if (!table) return html;

  // Find header row: TipTap tables don't use <thead>, so look for the row containing <th> elements
  const allRows = Array.from(table.querySelectorAll("tr"));
  const headerRow = allRows.find((row) => row.querySelector("th"));
  if (headerRow) {
    const scoreTh = doc.createElement("th");
    scoreTh.textContent = "Score";
    scoreTh.setAttribute("data-qbr-score-header", "true");
    headerRow.appendChild(scoreTh);

    const rationaleTh = doc.createElement("th");
    rationaleTh.textContent = "Score Rationale";
    rationaleTh.setAttribute("data-qbr-rationale-header", "true");
    headerRow.appendChild(rationaleTh);
  }

  // Add body columns (rows that contain <td> elements, not <th>)
  const bodyRows = allRows.filter((row) => row.querySelector("td") && !row.querySelector("th"));
  bodyRows.forEach((row, rowIndex) => {
    const scoreTd = doc.createElement("td");
    scoreTd.setAttribute("data-qbr-score", String(rowIndex));
    scoreTd.textContent = "\u200B"; // zero-width space so TipTap doesn't collapse the cell
    row.appendChild(scoreTd);

    const rationaleTd = doc.createElement("td");
    rationaleTd.setAttribute("data-qbr-rationale", String(rowIndex));
    rationaleTd.textContent = "\u200B";
    row.appendChild(rationaleTd);
  });

  return table.outerHTML;
}

interface TeamColumnConfig {
  owners: {
    owner_name: string;
    scores_list: Record<string, { score: string; rationale: string }>;
    active: boolean;
  }[];
  showCalculatedColumns: boolean;
}

export function injectTeamColumns(
  html: string,
  config: TeamColumnConfig
): string {
  if (!html) return html;

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const table = doc.querySelector("table");
  if (!table) return html;

  // Find header row: TipTap tables don't use <thead>, so look for the row containing <th> elements
  const allRows = Array.from(table.querySelectorAll("tr"));
  const headerRow = allRows.find((row) => row.querySelector("th"));
  if (headerRow) {
    const ths = Array.from(headerRow.querySelectorAll("th"));
    ths.slice(2).forEach((th) => th.remove());

    // Add owner columns
    for (const owner of config.owners) {
      const th = doc.createElement("th");
      th.setAttribute("data-qbr-owner-header", "true");
      th.textContent = getOwnerDisplayName(owner.owner_name);
      headerRow.appendChild(th);
    }

    // Add calculated columns
    if (config.showCalculatedColumns) {
      const avgTh = doc.createElement("th");
      avgTh.setAttribute("data-qbr-calc-header", "avg");
      avgTh.textContent = "Objective Avg";
      headerRow.appendChild(avgTh);

      const varTh = doc.createElement("th");
      varTh.setAttribute("data-qbr-calc-header", "var");
      varTh.textContent = "Objective Var";
      headerRow.appendChild(varTh);
    }
  }

  // Keep only first 2 columns in body rows (rows with <td>, not <th>)
  const bodyRows = allRows.filter((row) => row.querySelector("td") && !row.querySelector("th"));
  bodyRows.forEach((row, rowIndex) => {
    const tds = Array.from(row.querySelectorAll("td"));
    tds.slice(2).forEach((td) => td.remove());

    // Add owner score cells
    for (let ownerIndex = 0; ownerIndex < config.owners.length; ownerIndex++) {
      const td = doc.createElement("td");
      td.setAttribute("data-qbr-team-score", String(rowIndex));
      td.setAttribute("data-qbr-owner-index", String(ownerIndex));
      td.textContent = "\u200B";
      row.appendChild(td);
    }

    // Add calculated cells
    if (config.showCalculatedColumns) {
      const avgTd = doc.createElement("td");
      avgTd.setAttribute("data-qbr-team-avg", String(rowIndex));
      avgTd.textContent = "\u200B";
      row.appendChild(avgTd);

      const varTd = doc.createElement("td");
      varTd.setAttribute("data-qbr-team-var", String(rowIndex));
      varTd.textContent = "\u200B";
      row.appendChild(varTd);
    }
  });

  // Add summary row
  if (config.owners.length > 0) {
    let tbody = table.querySelector("tbody");
    if (!tbody) {
      tbody = doc.createElement("tbody");
      table.appendChild(tbody);
    }

    const summaryRow = doc.createElement("tr");
    summaryRow.setAttribute("data-qbr-summary-row", "true");

    const labelTd = doc.createElement("td");
    labelTd.setAttribute("colspan", "2");
    labelTd.setAttribute("data-qbr-summary-label", "true");
    labelTd.textContent = "Average score per person";
    summaryRow.appendChild(labelTd);

    for (let ownerIndex = 0; ownerIndex < config.owners.length; ownerIndex++) {
      const td = doc.createElement("td");
      td.setAttribute("data-qbr-summary-score", String(ownerIndex));
      td.textContent = "\u200B";
      summaryRow.appendChild(td);
    }

    if (config.showCalculatedColumns) {
      const avgTd = doc.createElement("td");
      avgTd.setAttribute("data-qbr-summary-avg", "true");
      avgTd.textContent = "\u200B";
      summaryRow.appendChild(avgTd);

      const varTd = doc.createElement("td");
      varTd.setAttribute("data-qbr-summary-var", "true");
      varTd.textContent = "\u200B";
      summaryRow.appendChild(varTd);
    }

    tbody.appendChild(summaryRow);
  }

  return table.outerHTML;
}

function getOwnerDisplayName(name?: string): string {
  if (!name) return "";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0];
  const first = parts[0];
  const lastInitial = parts[parts.length - 1][0]?.toUpperCase() ?? "";
  return `${first} ${lastInitial}`.trim();
}
